export interface Zone {
  id: string
  name: string
  minPercent: number // Percentage of threshold (0-200)
  maxPercent: number // Percentage of threshold (0-200)
  color: string
  description?: string
}

export interface ZoneModel {
  id: string
  name: string
  type: 'heart_rate' | 'pace' | 'power'
  zones: Zone[]
}

export interface CalculatedZones {
  hr: CalculatedZone[]
  pace: CalculatedZone[]
  power: CalculatedZone[]
}

export interface CalculatedZone extends Zone {
  minValue: number | null // Actual value (bpm, min/km seconds, or watts)
  maxValue: number | null // Actual value (bpm, min/km seconds, or watts)
}

export interface ZoneConfiguration {
  id?: string
  athlete_id: string
  zone_model_id: string
  zones: Zone[]
  created_at?: string
  updated_at?: string
}

// Default zone colors from HeartRateHistogram.tsx
export const DEFAULT_ZONE_COLORS = {
  z1: '#22C55E', // Green - Recovery
  z2: '#84CC16', // Lime green - Endurance
  z3: '#FBBF24', // Amber/Yellow - Tempo
  z4: '#F97316', // Orange - Threshold
  z5: '#DC2626', // Red - VO2max
  z6: '#B91C1C', // Dark red - Anaerobic
  z7: '#7F1D1D', // Darker red - Neuromuscular
}

// Default HR Zone Model (5 zones)
export const DEFAULT_HR_ZONES: ZoneModel = {
  id: 'hr_5zone',
  name: '5-Zone Heart Rate',
  type: 'heart_rate',
  zones: [
    {
      id: 'hr_z1',
      name: 'Z1 – Recovery',
      minPercent: 0,
      maxPercent: 82, // Up to VT1 - 5 bpm (assuming VT1 ~140, this is ~82% of VT1)
      color: DEFAULT_ZONE_COLORS.z1,
      description: 'Easy recovery pace, conversational',
    },
    {
      id: 'hr_z2',
      name: 'Z2 – Endurance',
      minPercent: 82,
      maxPercent: 100, // VT1 - 5 to VT1
      color: DEFAULT_ZONE_COLORS.z2,
      description: 'Aerobic endurance building',
    },
    {
      id: 'hr_z3',
      name: 'Z3 – Tempo',
      minPercent: 100,
      maxPercent: 97, // VT1 to VT2 - 5 (using VT2 as reference)
      color: DEFAULT_ZONE_COLORS.z3,
      description: 'Comfortably hard, sustainable',
    },
    {
      id: 'hr_z4',
      name: 'Z4 – Threshold',
      minPercent: 97,
      maxPercent: 100, // VT2 - 5 to VT2
      color: DEFAULT_ZONE_COLORS.z4,
      description: 'Lactate threshold, hard effort',
    },
    {
      id: 'hr_z5',
      name: 'Z5 – VO2max',
      minPercent: 100,
      maxPercent: 110, // Above VT2, up to ~110% of VT2
      color: DEFAULT_ZONE_COLORS.z5,
      description: 'Maximum aerobic effort',
    },
  ],
}

// Default Pace/Tempo Zone Model (6 zones)
export const DEFAULT_PACE_ZONES: ZoneModel = {
  id: 'pace_6zone',
  name: '6-Zone Pace',
  type: 'pace',
  zones: [
    {
      id: 'pace_z1',
      name: 'Z1 – Easy',
      minPercent: 120,
      maxPercent: 110, // Slower than VT1 pace (higher time = slower pace)
      color: DEFAULT_ZONE_COLORS.z1,
      description: 'Easy running, very comfortable',
    },
    {
      id: 'pace_z2',
      name: 'Z2 – Moderate',
      minPercent: 110,
      maxPercent: 100, // VT1 + 10% to VT1
      color: DEFAULT_ZONE_COLORS.z2,
      description: 'Moderate aerobic effort',
    },
    {
      id: 'pace_z3',
      name: 'Z3 – Tempo',
      minPercent: 100,
      maxPercent: 105, // VT1 to slightly slower than VT2
      color: DEFAULT_ZONE_COLORS.z3,
      description: 'Comfortably hard tempo',
    },
    {
      id: 'pace_z4',
      name: 'Z4 – Threshold',
      minPercent: 105,
      maxPercent: 100, // Around VT2 pace
      color: DEFAULT_ZONE_COLORS.z4,
      description: 'Lactate threshold pace',
    },
    {
      id: 'pace_z5',
      name: 'Z5 – VO2max',
      minPercent: 100,
      maxPercent: 95, // VT2 to 5% faster
      color: DEFAULT_ZONE_COLORS.z5,
      description: 'Hard intervals, VO2max',
    },
    {
      id: 'pace_z6',
      name: 'Z6 – Anaerobic',
      minPercent: 95,
      maxPercent: 85, // Faster than VO2max pace
      color: DEFAULT_ZONE_COLORS.z6,
      description: 'Sprint intervals, anaerobic',
    },
  ],
}

// Default Power Zone Model (7 zones)
export const DEFAULT_POWER_ZONES: ZoneModel = {
  id: 'power_7zone',
  name: '7-Zone Power',
  type: 'power',
  zones: [
    {
      id: 'power_z1',
      name: 'Z1 – Active Recovery',
      minPercent: 0,
      maxPercent: 55,
      color: DEFAULT_ZONE_COLORS.z1,
      description: 'Very light effort, active recovery',
    },
    {
      id: 'power_z2',
      name: 'Z2 – Endurance',
      minPercent: 56,
      maxPercent: 75,
      color: DEFAULT_ZONE_COLORS.z2,
      description: 'Aerobic endurance building',
    },
    {
      id: 'power_z3',
      name: 'Z3 – Tempo',
      minPercent: 76,
      maxPercent: 90,
      color: DEFAULT_ZONE_COLORS.z3,
      description: 'Steady sustainable effort',
    },
    {
      id: 'power_z4',
      name: 'Z4 – Lactate Threshold',
      minPercent: 91,
      maxPercent: 105,
      color: DEFAULT_ZONE_COLORS.z4,
      description: 'Functional Threshold Power (FTP)',
    },
    {
      id: 'power_z5',
      name: 'Z5 – VO2 Max',
      minPercent: 106,
      maxPercent: 120,
      color: DEFAULT_ZONE_COLORS.z5,
      description: 'Maximum aerobic power',
    },
    {
      id: 'power_z6',
      name: 'Z6 – Anaerobic Capacity',
      minPercent: 121,
      maxPercent: 150,
      color: DEFAULT_ZONE_COLORS.z6,
      description: 'Short hard efforts',
    },
    {
      id: 'power_z7',
      name: 'Z7 – Neuromuscular',
      minPercent: 151,
      maxPercent: 200,
      color: DEFAULT_ZONE_COLORS.z7,
      description: 'Maximum sprint power',
    },
  ],
}
